import type { ContentHash, ExamId, IsoDateTime, QuestionId, SessionId } from '../model/brands.ts';
import type { CanonicalExam } from '../model/canonical.ts';
import type {
  Confidence,
  QuestionReport,
  QuestionScore,
  Response,
} from '../model/session.ts';
import type { TimeSegment, SessionClock } from './clock.ts';
import { createClock, elapsedMs } from './clock.ts';
import type { SessionBlueprint } from './blueprint.ts';
import type { ModePreset, SelectionCriteria, SessionPolicy } from './policy.ts';

export type SessionStatus =
  | 'active'
  | 'paused'
  | 'confirming-submit'
  | 'completed'
  | 'reviewing';

export interface QuestionEntry {
  readonly response: Response | null;
  readonly markedForReview: boolean;
  readonly confidence: Confidence | null;
  readonly visits: number;
  readonly changedAnswerCount: number;
  readonly segments: readonly TimeSegment[];
  readonly firstAnsweredAt: IsoDateTime | null;
  /** When feedback was shown, in practice mode. Locks the answer once set. */
  readonly revealedAt: IsoDateTime | null;
  /** The candidate's own verdict on a question the app could not score. */
  readonly selfGrade: boolean | null;
  /**
   * Whatever the candidate wanted to say to themselves about this question.
   *
   * Empty means no note — rather than null — so that clearing one and never writing one are the
   * same state, and nothing downstream has to tell them apart.
   */
  readonly note: string;
}

/**
 * The longest a note may be.
 *
 * Not a judgement about how much anyone should write: a cap exists because notes are the only part
 * of a session that grows without bound, and a saved session has to fit in a browser's storage
 * quota alongside everything else.
 */
export const MAX_NOTE_LENGTH = 2000;

export interface ExamRef {
  readonly examId: ExamId;
  readonly contentHash: ContentHash;
  readonly schemaVersion: string;
  readonly contentVersion: string;
  readonly title: string;
  readonly questionCount: number;
}

export interface SessionState {
  readonly id: SessionId;
  readonly status: SessionStatus;
  /** What the candidate typed on the setup screen, for their own records. Empty means unnamed. */
  readonly candidateName: string;
  /**
   * True when the candidate pressed Pause, as opposed to the tab being hidden.
   *
   * The distinction is load-bearing: coming back to a tab must not restart a session someone
   * deliberately stopped, and the two would otherwise be the same `paused` status.
   */
  readonly pausedByUser: boolean;
  readonly modePreset: ModePreset;
  readonly policy: SessionPolicy;
  readonly criteria: SelectionCriteria;
  readonly blueprint: SessionBlueprint;
  readonly examRef: ExamRef;
  readonly startedAt: IsoDateTime;
  readonly submittedAt: IsoDateTime | null;
  /** Index into `blueprint.questionOrder`. */
  readonly cursor: number;
  readonly entries: Readonly<Record<string, QuestionEntry>>;
  readonly reports: readonly QuestionReport[];
  readonly clock: SessionClock;
  /** Filled in at submission; empty before. */
  readonly scores: Readonly<Record<string, QuestionScore>>;
}

export function emptyEntry(at: number): QuestionEntry {
  return {
    response: null,
    markedForReview: false,
    confidence: null,
    visits: 0,
    changedAnswerCount: 0,
    segments: [{ startedAt: at, endedAt: at }],
    firstAnsweredAt: null,
    revealedAt: null,
    selfGrade: null,
    note: '',
  };
}

export interface CreateSessionInput {
  readonly id: SessionId;
  readonly candidateName?: string;
  readonly exam: CanonicalExam;
  readonly blueprint: SessionBlueprint;
  readonly policy: SessionPolicy;
  readonly criteria: SelectionCriteria;
  readonly modePreset: ModePreset;
  readonly startedAt: IsoDateTime;
  readonly monotonicNow: number;
}

/**
 * The state a session starts in.
 *
 * Every question drawn gets an entry immediately, closed and at zero. Creating entries lazily would
 * make "never visited" indistinguishable from "not drawn" — the same ambiguity the exported session
 * avoids by writing out unanswered questions rather than omitting them.
 */
export function createSession(input: CreateSessionInput): SessionState {
  const entries: Record<string, QuestionEntry> = {};
  for (const questionId of input.blueprint.questionOrder) {
    entries[questionId] = emptyEntry(input.monotonicNow);
  }

  const state: SessionState = {
    id: input.id,
    status: 'active',
    candidateName: (input.candidateName ?? '').trim(),
    pausedByUser: false,
    modePreset: input.modePreset,
    policy: input.policy,
    criteria: input.criteria,
    blueprint: input.blueprint,
    examRef: {
      examId: input.exam.meta.id,
      contentHash: input.exam.contentHash,
      schemaVersion: input.exam.schemaVersion,
      contentVersion: input.exam.meta.contentVersion,
      title: input.exam.meta.title,
      questionCount: input.exam.questions.length,
    },
    startedAt: input.startedAt,
    submittedAt: null,
    cursor: 0,
    entries,
    reports: [],
    clock: createClock(input.policy.timeLimitMs, input.monotonicNow),
    scores: {},
  };

  // The first question is already on screen, so its timer is already running.
  return enterQuestion(state, 0, input.monotonicNow);
}

/**
 * Makes a session restored from storage safe to continue.
 *
 * Every recorded time is a `performance.now()` reading, and that clock restarts from zero on every
 * page load. Continuing without this would compute durations against a new origin and produce a
 * session that had apparently run for negative time, or for as long as the browser had been open.
 *
 * So each series of segments collapses into a single closed segment holding the total it had
 * accumulated. The totals survive; the origins do not, because they no longer mean anything. The
 * session comes back paused, and deliberately by the user: the hours between saving and reopening
 * are not exam time, and neither is the moment spent finding the file again.
 */
export function rebaseSession(state: SessionState, at: number): SessionState {
  const collapse = (segments: readonly TimeSegment[]): readonly TimeSegment[] => [
    { startedAt: 0, endedAt: elapsedMs(segments, at) },
  ];

  const entries: Record<string, QuestionEntry> = {};
  for (const [questionId, entry] of Object.entries(state.entries)) {
    entries[questionId] = { ...entry, segments: collapse(entry.segments) };
  }

  return {
    ...state,
    status: state.status === 'completed' || state.status === 'reviewing' ? state.status : 'paused',
    pausedByUser: true,
    entries,
    clock: { ...state.clock, segments: collapse(state.clock.segments) },
  };
}

export function currentQuestionId(state: SessionState): QuestionId | undefined {
  return state.blueprint.questionOrder[state.cursor];
}

/** Opens the timer of the question at `index` and counts the visit. */
export function enterQuestion(state: SessionState, index: number, at: number): SessionState {
  const questionId = state.blueprint.questionOrder[index];
  if (questionId === undefined) {
    return state;
  }

  const entry = state.entries[questionId];
  if (entry === undefined) {
    return state;
  }

  return {
    ...state,
    cursor: index,
    entries: {
      ...state.entries,
      [questionId]: {
        ...entry,
        visits: entry.visits + 1,
        segments: [...entry.segments, { startedAt: at, endedAt: null }],
      },
    },
  };
}

/** Closes the timer of the question currently on screen. */
export function leaveQuestion(state: SessionState, at: number): SessionState {
  const questionId = currentQuestionId(state);
  if (questionId === undefined) {
    return state;
  }

  const entry = state.entries[questionId];
  const last = entry?.segments[entry.segments.length - 1];
  if (entry === undefined || last?.endedAt !== null) {
    return state;
  }

  return {
    ...state,
    entries: {
      ...state.entries,
      [questionId]: {
        ...entry,
        segments: [
          ...entry.segments.slice(0, -1),
          { startedAt: last.startedAt, endedAt: Math.max(at, last.startedAt) },
        ],
      },
    },
  };
}
